import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags';
import type { RemixHistoryEntryForUpdate } from '@atlassian/malleable-ui-components/types';

import { onStartRemixThinking } from '../../../editor-commands/start-remix-thinking';
import type {
	GetRemixVariantFloatingToolbarButton,
	MalleableUiPlugin,
} from '../../../malleableUiPluginType';
import { getHistoryEntryForUpdate } from '../../../pm-plugins/remix-history/getHistoryEntryForUpdate';

export function shouldShowRemixVariant({
	node,
	schema,
	state,
}: {
	node: PMNode;
	schema: Schema;
	state: EditorState;
}): RemixHistoryEntryForUpdate | undefined {
	const { media, mediaSingle } = schema.nodes;
	const isMaui = node.attrs.extensionKey === 'native-embed:maui';
	const isWhiteboard = node.attrs.extensionKey === 'native-embed:maui-whiteboard';
	if (isMaui || isWhiteboard) {
		const url = node.attrs.parameters?.macroParams?.url?.value;
		return getHistoryEntryForUpdate({ state, url });
	}

	const isMedia = node.type === media || node.type === mediaSingle;
	if (isMedia) {
		const mediaNode = node.type === media ? node : node.firstChild;
		if (mediaNode) {
			const fieldId = mediaNode.attrs.id;
			if (fieldId) {
				return getHistoryEntryForUpdate({ state, fieldId });
			}
		}
	}
}

export function makeRemixVariantFloatingToolbarButton({
	api,
}: {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
}): GetRemixVariantFloatingToolbarButton {
	return ({ schema, state, node }) => {
		if (!fg('platform_editor_ai_remix_toolbar')) {
			return;
		}
		if (!node) {
			return;
		}

		const historyEntry = shouldShowRemixVariant({ node, schema, state });
		if (!historyEntry) {
			return;
		}

		const nextSubtypeByType: Record<string, string[]> = {
			chart: ['line-chart', 'area-chart', 'bar-chart', 'funnel-chart', 'pie-chart', 'gantt-chart'],
			diagram: ['mindmap', 'flowchart'],
			infographic: ['charlie', 'minimalist', 'corporate-doodle'],
			'visual-aid': [
				'flip-cards',
				'quadrant',
				'tree-map',
				'horizontal-timeline',
				'map',
				'org-chart',
				'gantt-chart',
				'image',
			],
		};
		const subtypeOptions = nextSubtypeByType[historyEntry.dynamicUiType] ?? nextSubtypeByType.chart;
		const currentSubtypeIndex = historyEntry.dynamicUiSubtype
			? subtypeOptions.indexOf(historyEntry.dynamicUiSubtype)
			: -1;
		const dynamicUiSubtypeForUpdate =
			subtypeOptions[(currentSubtypeIndex + 1) % subtypeOptions.length];

		const disabled = Boolean(
			api?.malleableUi?.sharedState.currentState()?.isRemixModalOpen ||
				api?.malleableUi?.sharedState.currentState()?.isThinking ||
				api?.aiExperience?.sharedState.currentState()?.areAiEntryPointsDisabled,
		);

		return {
			id: 'editor.malleable-ui.test-update',
			type: 'button',
			title: 'Test Update',
			tooltipContent: 'Test Update',
			showTitle: true,
			hideTooltipOnClick: false,
			supportsViewMode: false,
			disabled,
			onClick: () => {
				onStartRemixThinking({
					api,
					options: {
						isUpdate: true,
						historyEntryForUpdate: historyEntry,
						dynamicUiSubtypeForUpdate: dynamicUiSubtypeForUpdate || 'area-chart',
					},
				});
				return true;
			},
		};
	};
}
